import { patientCaseContentSchema } from '@llx/common';
import * as Sentry from '@sentry/react';
import { z } from 'zod';

import type { PatientCaseContent } from './generated';

export class ValidationError extends Error {
  constructor(
    message: string,
    public zodError: z.ZodError
  ) {
    super(message);
    this.name = 'ValidationError';

    Sentry.captureException(this, {
      tags: { errorType: 'validation' },
      contexts: {
        validation: { errorCount: zodError.errors.length, errors: formatZodErrors(zodError) },
      },
      fingerprint: ['validation-error', message],
    });
  }
}

/**
 * Validates and parses a case configuration JSON from S3
 * Throws ValidationError if validation fails
 */
export function validateCaseConfig(data: unknown): PatientCaseContent {
  try {
    return patientCaseContentSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[validateCaseConfig] Validation failed:', error.errors);
      throw new ValidationError('Invalid case configuration structure', error);
    }
    throw error;
  }
}

/**
 * Safe validation that returns either validated data or error
 */
export function safeParseCaseConfig(data: unknown) {
  return patientCaseContentSchema.safeParse(data);
}

/**
 * Formats Zod errors for readable display
 */
export function formatZodErrors(error: z.ZodError): string[] {
  return error.errors.map((err) => {
    const path = err.path.join('.');
    return `${path}: ${err.message}`;
  });
}
